import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { logsApi, devicesApi } from '../../api/client';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';

const LEVELS = ['', 'DEBUG', 'INFO', 'WARN', 'ERROR'] as const;

const levelColors: Record<string, string> = {
  DEBUG: 'bg-gray-100 text-gray-800',
  INFO: 'bg-blue-100 text-blue-800',
  WARN: 'bg-yellow-100 text-yellow-800',
  ERROR: 'bg-red-100 text-red-800',
};

export default function LogsPage() {
  const [deviceId, setDeviceId] = useState('');
  const [level, setLevel] = useState('');
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const limit = 50;

  const { data: devices } = useQuery({
    queryKey: ['devices'],
    queryFn: async () => {
      const resp = await devicesApi.list();
      return resp.data;
    },
  });

  const { data: logsData, isLoading, refetch } = useQuery({
    queryKey: ['logs', { deviceId, level, search, startDate, endDate, page }],
    queryFn: async () => {
      const params: any = { page, limit };
      if (deviceId) params.deviceId = deviceId;
      if (level) params.level = level;
      if (search) params.search = search;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const resp = await logsApi.getLogs(params);
      return resp.data;
    },
    refetchInterval: autoRefresh ? 30000 : false,
  });

  const logs = logsData?.data || [];
  const total = logsData?.total || 0;
  const totalPages = logsData?.totalPages || 1;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Device Logs</h1>
          <p className="text-gray-600">View logs from tracked devices</p>
        </div>
        <div className="flex gap-2 items-center">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            Auto-refresh
          </label>
          <Button variant="outline" onClick={() => refetch()}>
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <select
              value={deviceId}
              onChange={(e) => { setDeviceId(e.target.value); setPage(1); }}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">All Devices</option>
              {(devices || []).map((d: any) => (
                <option key={d.id} value={d.id}>
                  {d.name} ({d.user?.firstName} {d.user?.lastName}) — {d.id.slice(0, 8)}
                </option>
              ))}
            </select>

            <select
              value={level}
              onChange={(e) => { setLevel(e.target.value); setPage(1); }}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">All Levels</option>
              {LEVELS.filter(Boolean).map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>

            <Input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              placeholder="Start date"
            />
            <Input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              placeholder="End date"
            />

            <Input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search messages..."
            />
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading logs...</div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No logs found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Timestamp</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Device</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Level</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Module</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Message</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {logs.map((log: any) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 whitespace-nowrap text-gray-500 text-xs">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="text-xs">
                          <div className="font-medium">{log.device?.name}</div>
                          <div className="text-gray-400">
                            {log.device?.user?.firstName} {log.device?.user?.lastName}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${levelColors[log.level] || 'bg-gray-100 text-gray-800'}`}>
                          {log.level}
                        </span>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-600 font-mono">
                        {log.module}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-700 max-w-md truncate">
                        {log.message}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center text-sm text-gray-600">
          <span>Showing {logs.length} of {total} logs</span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Previous
            </Button>
            <span className="flex items-center px-3">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
